/*
 * -----------------------------------------------------------------------
 *  <copyright file="import-and-tr-setup-dialog/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import { useBoolean, useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GET_SITES } from 'queries/sites';
import { GET_STUDY, SAVE_CENTRAL_MONITORING_CONFIGURATION } from 'queries/studies';
import { SvtDependencyType } from 'types/graphql-global-types';
import { stringDateWithoutTimeFromUtc } from 'utils/convertor';
import { createArrayFromEnum } from 'utils/generator';
import NotificationService from 'utils/notification';
import { validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import Loading from 'views/components/core/loading';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import 'views/components/admin/studies/study-settings-dialogs/study-settings-dialogs.scss';
import './styles.scss';

const transformCMStudyData = (study: StudyFields) => ({
    importDefinitionsKey: study.importDefinitionsKey ?? '',
    volumeAdjustedScoring: study.volumeAdjustedScoring || false,
    siteStatusesToExclude: study.sitesStatusesToExclude.map((siteName: string) => ({
        name: siteName,
        id: siteName,
    })),
    scoreGreenThreshold: study.scoreGreenThreshold,
    scoreAmberThreshold: study.scoreAmberThreshold,
    volumeAdjustedScoringNumberField: study.volumeAdjustedScoringNumberField,
    scoreThreshold: +study.scoreThreshold,
    adjustmentWeight: study.adjustmentWeight,
    svtDependency: study.svtDependency,
    calculateAssessments: study.calculateAssessments,
    assessmentAssociation: study.assessmentAssociation,
    useInformedConsentedDate: study.useInformedConsentedDate,
    calculateCrfs: study.calculateCrfs,
    calculateVisits: study.calculateVisits,
    lastSubjectScreenedDate: stringDateWithoutTimeFromUtc(study.lastSubjectScreenedDate),
    lastSubjectEnrolledDate: stringDateWithoutTimeFromUtc(study.lastSubjectEnrolledDate),
    lastSubjectRandomisedDate: stringDateWithoutTimeFromUtc(study.lastSubjectRandomisedDate),
    automationActive: study.automationActive,
    studyAutomationColor: study.studyAutomationColor,
    controlsAutomationActive: study.controlsAutomationActive,
    controlsAutomationColor: study.controlsAutomationColor,
    integrationActive: study.integrationActive,
    integrationStudyNumber: study.integrationStudyNumber,
});

const getSvtDependencies = () =>
    createArrayFromEnum(SvtDependencyType)
        .slice(1)
        .map((dependency) => {
            dependency.active = true;
            return dependency;
        });

const ImportAndTRSetupDialog = ({
    study,
    open,
    onClose,
    canEdit,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
    canEdit: boolean;
}) => {
    const { t } = useTranslation();
    const [saveStudy] = useMutation(SAVE_CENTRAL_MONITORING_CONFIGURATION);
    const disabledSaveButton = useBoolean(true);
    const initialState = transformCMStudyData(study);
    const initialErrors = {
        scoreThreshold: '',
        adjustmentWeight: '',
        scoreGreenThreshold: '',
        scoreAmberThreshold: '',
        volumeAdjustedScoringNumberField: '',
        importDefinitionsKey: '',
    };
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialErrors });
    const [svtDependencies] = useState(getSvtDependencies);
    const [loadSitesData, { loading: loadingSites, data: dataSites }] = useLazyQuery(GET_SITES);

    const validate = (fieldName: string, value?: number | string): { [key: string]: string } | null => {
        let error = null;
        if (fieldName === 'importDefinitionsKey') {
            error = validateFields(value as string, fieldName, 50, false);
            setErrors({
                ...errors,
                ...error,
            });
            return;
        }
        setErrors({ ...errors, [fieldName]: error });
        return {
            [fieldName]: error,
        };
    };

    useEffect(() => {
        if (study && open) {
            setState(transformCMStudyData(study));

            if (!dataSites?.sites) {
                loadSitesData({ variables: { studyId: study.id } });
            }
        }
    }, [study, open]);

    useEffect(() => {
        disabledSaveButton.setValue(JSON.stringify(initialState) === JSON.stringify(state));
    }, [state]);

    const close = () => {
        disabledSaveButton.setTrue();
        setState({ ...initialState });
        setErrors({ ...initialErrors });
        onClose();
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: study.recordId }));
        onClose();
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const checkForErrors = () => !!Object.keys(errors).filter((item) => (errors as any)[item]).length;

    const onUpdate = () => {
        if (!checkForErrors()) {
            disabledSaveButton.setTrue();

            saveStudy({
                variables: {
                    studyId: study.id,
                    importDefinitionsKey: state.importDefinitionsKey,
                    volumeAdjustedScoring: state.volumeAdjustedScoring,
                    siteStatusesToExclude: state.siteStatusesToExclude.map((item: { name: string }) => item.name),
                    scoreGreenThreshold: state.scoreGreenThreshold,
                    scoreAmberThreshold: state.scoreAmberThreshold,
                    scoreThreshold: state.scoreThreshold,
                    volumeAdjustedScoringNumberField: state.volumeAdjustedScoringNumberField,
                    adjustmentWeight: state.adjustmentWeight,
                    svtDependency: state.svtDependency,
                    calculateAssessments: state.calculateAssessments,
                    assessmentAssociation: state.assessmentAssociation,
                    useInformedConsentedDate: state.useInformedConsentedDate,
                    calculateCrfs: state.calculateCrfs,
                    calculateVisits: state.calculateVisits,
                    lastSubjectScreenedDate: state.lastSubjectScreenedDate,
                    lastSubjectEnrolledDate: state.lastSubjectEnrolledDate,
                    lastSubjectRandomisedDate: state.lastSubjectRandomisedDate,
                    automationActive: state.automationActive,
                    studyAutomationColor: state.studyAutomationColor,
                    controlsAutomationActive: state.controlsAutomationActive,
                    controlsAutomationColor: state.controlsAutomationColor,
                    integrationActive: study.integrationActive,
                    integrationStudyNumber: study.integrationStudyNumber ?? '',
                },
                refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
            })
                .then(() => {
                    successRequest();
                })
                .catch(() => {
                    errorRequest();
                });
        }
    };

    const onChange = (fieldName: string) => (event: any) => {
        if (disabledSaveButton.value) {
            disabledSaveButton.setFalse();
        }
        setState({ ...state, [fieldName]: event.target.value });
        validate(fieldName, event.target.value);
    };

    const onChangeRules = (
        fieldName:
            | 'calculateVisits'
            | 'calculateCrfs'
            | 'calculateAssessments'
            | 'assessmentAssociation'
            | 'useInformedConsentedDate',
    ) => {
        disabledSaveButton.setFalse();

        if (
            (fieldName === 'calculateAssessments' || fieldName === 'assessmentAssociation') &&
            !state[fieldName] === true
        )
            setState((prevState) => ({
                ...prevState,
                [fieldName]: !prevState[fieldName],
                // Set this field as true if calculateAssessments and assessmentAssociation are true
                calculateCrfs:
                    fieldName === 'calculateAssessments'
                        ? prevState.assessmentAssociation
                        : prevState.calculateAssessments,
                calculateVisits: true,
            }));
        else if (fieldName === 'calculateCrfs' && !state[fieldName] === true)
            setState((prevState) => ({
                ...prevState,
                [fieldName]: !prevState[fieldName],
                calculateVisits: true,
            }));
        else
            setState((prevState) => ({
                ...prevState,
                [fieldName]: !prevState[fieldName],
            }));
    };

    const onChangeSvtDependencies = () => (event: any) => {
        disabledSaveButton.setFalse();
        setState({
            ...state,
            svtDependency: event.target.value,
        });
    };

    if (loadingSites) {
        return <Loading float />;
    }

    return (
        <CustomDialog className="import-tr-setup" onClose={close} fullWidth scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="import-tr-setup-title">
                {t('admin:importAndTRSetupWithId', { id: study.recordId })}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="import-tr-setup__content">
                <div className="import-tr-setup__row-wrap">
                    <FormControl variant="outlined" className="form-action-type not-full">
                        <TextField
                            label={t('admin:importFileName')}
                            id="import-tr-importDefinitionsKey"
                            value={state.importDefinitionsKey}
                            onChange={onChange('importDefinitionsKey')}
                            placeholder={t('admin:enterImportFileName')}
                            variant="outlined"
                            fullWidth
                            color="secondary"
                            className="before-select"
                            helperText={
                                <CharactersRemaining
                                    id="importDefinitionsKey-text"
                                    length={state.importDefinitionsKey.length}
                                    max={50}
                                    error={errors.importDefinitionsKey}
                                />
                            }
                            error={!!errors.importDefinitionsKey}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-action-type not-full">
                        <Checkbox
                            rounded="none"
                            checked={state.assessmentAssociation}
                            onChange={() => onChangeRules('assessmentAssociation')}
                            label={t('admin:assessmentAssociation')}
                            data-testid="assessment-association-checkbox"
                            size="medium"
                        />
                    </FormControl>
                </div>
                <div className="import-tr-setup__row-wrap">
                    <FormControl variant="outlined" className="form-action-type not-full">
                        <SelectWithCategories
                            customClassName="import-tr-setup__select-association"
                            showWarning={false}
                            fieldSelect="svtAssociation"
                            onChange={onChangeSvtDependencies}
                            label={t('admin:svtAssociation')}
                            showLabels={false}
                            labelField="svtAssociation"
                            dataArray={svtDependencies}
                            customId="import-tr-select-association"
                            currentState={state.svtDependency}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-action-type not-full">
                        <Checkbox
                            rounded="none"
                            checked={state.useInformedConsentedDate}
                            onChange={() => onChangeRules('useInformedConsentedDate')}
                            label={t('admin:useInformedConsentedDate')}
                            data-testid="use-informed-consented-date-checkbox"
                            size="medium"
                        />
                    </FormControl>
                </div>
                <OutlinedWrapper
                    customClass="import-tr-setup__data-rules-association-wrapper"
                    label={t('admin:transformationRules')}
                >
                    <div className="import-tr-setup__data-rules-wrapper">
                        <Checkbox
                            rounded="none"
                            checked={state.calculateVisits}
                            onChange={() => onChangeRules('calculateVisits')}
                            disabled={!!state.calculateAssessments || !!state.calculateCrfs}
                            label={t('admin:calculateVisits')}
                            labelClassName={cn({
                                'checkbox-label-disabled': state.calculateAssessments || state.calculateCrfs,
                            })}
                            data-testid="calculate-visits-checkbox"
                            size="medium"
                        />
                        <Checkbox
                            rounded="none"
                            checked={state.calculateCrfs}
                            disabled={!!state.calculateAssessments && !!state.assessmentAssociation}
                            onChange={() => onChangeRules('calculateCrfs')}
                            label={t('admin:calculateCrfs')}
                            labelClassName={cn({
                                'checkbox-label-disabled': state.calculateAssessments && state.assessmentAssociation,
                            })}
                            data-testid="calculate-crfs-checkbox"
                            size="medium"
                        />
                        <Checkbox
                            rounded="none"
                            checked={state.calculateAssessments}
                            onChange={() => onChangeRules('calculateAssessments')}
                            label={t('admin:calculateAssessments')}
                            data-testid="calculate-assessments-checkbox"
                            size="medium"
                        />
                    </div>
                </OutlinedWrapper>
            </DialogContent>
            <DialogActions className="import-tr-setup__actions dialog-actions flex-end">
                <SaveButton
                    id="import-tr-save"
                    disabled={
                        disabledSaveButton.value ||
                        Object.keys(errors).filter((item) => (errors as any)[item]).length > 0 ||
                        !canEdit
                    }
                    text="update"
                    onClick={onUpdate}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default ImportAndTRSetupDialog;
